import { nanoid } from 'nanoid';

/**
 * Represents an observed event that has taken place
 */
export type IEvent<TType extends string = string, TPayload = unknown> = {
    /**
     * Universally unique identifier for the event
     */
    readonly id: string;

    /**
     * Timestamp when the event was created (not necessarily when it took place)
     */
    readonly timestamp: number;

    /**
     * The type of the event, used to infer the payload type
     */
    readonly type: TType;

    /**
     * The namespace of the event, used in combination with the type to disambiguate between
     * unrelated events that use the same event-type.
     * This also allows events of the same type but differing namespaces to use different payload definitions.
     */
    readonly namespace?: string;

    /**
     * The payload of the event, containing the event-specific data
     */
    readonly payload: TPayload;
};

/**
 * Represents a mapping of event types to their payloads
 */
type EventMap = Record<string, unknown>;

/**
 * Represents a subscription's response state when an event is emitted
 */
type SubscriptionResponse = {
    /**
     * The ID of the subscription
     */
    readonly id: string;
    /**
     * Indicates this is a synchronous subscription
     */
    isAsync: boolean;
    /**
     * Indicates if the synchronous subscription completed successfully
     */
    successful: boolean;
};

/**
 * Represents a subscription's response state when an event is emitted
 */
type IndividualSubscriptionResponse = {
    /**
     * The ID of the subscription
     */
    readonly id: string;
    /**
     * Indicates this is a synchronous subscription
     */
    isAsync: false;
    /**
     * Indicates if the synchronous subscription completed successfully
     */
    successful: boolean;
} | {
    /**
     * The ID of the subscription
     */
    readonly id: string;
    /**
     * Indicates this is an asynchronous subscription
     */
    isAsync: true;
    /**
     * Promise that resolves to true if the async subscription completes successfully, false otherwise
     */
    successful: Promise<boolean>;
};

/**
 * Represents the synchronous response returned from emitting an event
 */
export type EmitResponse<TType extends string, TPayload> = {
    /**
     * The event that was emitted
     */
    readonly event: IEvent<TType, TPayload>;

    /**
     * The response state of the subscriptions that were notified of the event
     */
    readonly subscriptions: IndividualSubscriptionResponse[];

    /**
     * A promise that resolves when all subscriptions have settled
     */
    readonly onSettled: Promise<SubscriptionResponse[]>;
}

/**
 * Represents a specific event emitter for a particular event type and payload
 */
interface ISpecificEventEmitter<TType extends string, TPayload> {

    /**
     * Emits an event with the specified payload
     * @param payload The payload of the event
     */
    emit(payload: TPayload): EmitResponse<TType, TPayload>; //TODO: support a cause event, support an async emit

    /**
     * Emits an event with a looser payload type allowing extra properties
     * @param payload The payload of the event
     */
    emitLoose<TLoosePayload extends TPayload>(payload: TLoosePayload): EmitResponse<TType, TLoosePayload>; //TODO: support a cause event, support an async emit
}

/**
 * Represents a mapping of event types to their specific emitters or sub-emitters
 */
type EmitterEvents<TEvents extends EventMap> = {
    [K in keyof TEvents]: ISpecificEventEmitter<K & string, TEvents[K]>
}

export type IEventEmitter<TEvents extends EventMap = EventMap> = {
    readonly events: EmitterEvents<TEvents>;

    emit<TKey extends keyof TEvents>(
        type: TKey & string,
        payload: TEvents[TKey]
    ): EmitResponse<TKey & string, TEvents[TKey]>;
    emitLoose<TKey extends keyof TEvents, TPayload extends TEvents[TKey] = TEvents[TKey]>(
        type: TKey & string,
        payload: TPayload
    ): EmitResponse<TKey & string, TPayload>;
}

export type EventListener<TEvents extends IEvent[] = IEvent[]> = (payload: TEvents[number]['payload'], event: TEvents[number]) => void | Promise<void>;

interface ISpecificSource<TType extends string, TPayload> {
    on(
        listener: EventListener<[IEvent<TType & string, TPayload>]>
    ): Subscription;
}

type ISourceEvents<TEvents extends EventMap> = {
    [K in keyof TEvents]: ISpecificSource<K & string, TEvents[K]>
}

export type Subscription = {
    readonly id:string;
    unsubscribe: () => boolean;
}

export type IEventSource<TEvents extends EventMap = EventMap> = {
    readonly events: ISourceEvents<TEvents>;

    on<TKey extends keyof TEvents>(
        type: TKey & string,
        listener: EventListener<[IEvent<TKey & string, TEvents[TKey]>]>
    ): Subscription;

    on<TKeys extends ReadonlyArray<keyof TEvents>>(
        types: TKeys, 
        listener: EventListener<[{ [K in TKeys[number]]: IEvent<K & string, TEvents[K]> }[TKeys[number]]]>
    ): Subscription;

    on(
        listener: EventListener<[{ [K in keyof TEvents]: IEvent<K & string, TEvents[K]>}[keyof TEvents]]>
    ): Subscription;
}
type InferSourceEvents<TSource extends IEventSource> = TSource extends IEventSource<infer TEvents>
    ? TEvents
    : never;

type EventBusMap = Record<string, IEventSource>;

export type IEventBus<TEmitterMap extends EventBusMap> = {
    readonly namespaces: TEmitterMap;
    on(listener: EventListener): Subscription;
    on<TKeys extends ReadonlyArray<keyof TEmitterMap>>(
        namespaces: TKeys, 
        listener: EventListener<[{ [K in TKeys[number]]: { [E in keyof InferSourceEvents<TEmitterMap[K]>]: IEvent<E & string, InferSourceEvents<TEmitterMap[K]>[E]> }[keyof InferSourceEvents<TEmitterMap[K]>] }[TKeys[number]]]>
    ): Subscription;
}

function createEvent<TType extends string = string, TPayload = unknown>(
    type: TType,
    payload: TPayload,
    namespace?: string
): IEvent<TType, TPayload> {
    return {
        id: nanoid(),
        timestamp: Date.now(),
        type,
        namespace,
        payload,
    };
}

export function createEventEmitter<TEvents extends EventMap>(errorHandler:(message:string, error:unknown)=>void = () => {}): IEventEmitter<TEvents> & IEventSource<TEvents> {
    const listeners = new Map<string, Map<string, EventListener>>();
    const broadcastListeners = new Map<string, EventListener>();

    const emitEvent = <TType extends string = string, TPayload = unknown>(
        type: TType,
        payload: TPayload
    ): EmitResponse<TType, TPayload> => {
        const event = createEvent(type, payload);
        const typeListeners = listeners.get(type) ?? new Map<string, EventListener>();

        const subscriptions = [... Array.from(typeListeners.entries()), ...Array.from(broadcastListeners.entries())]
        const responses: IndividualSubscriptionResponse[] = subscriptions.map(([id, listener]) => {
            try {
                const result = listener(payload, event);
                const isAsync = result instanceof Promise;
                if (isAsync) {
                    const successful = result
                        .then(() => true)
                        .catch((error) => {
                            errorHandler(`Error in async event listener for ${type}:`, error);
                            return false;
                        });

                    return {
                        id,
                        isAsync,
                        successful,
                    } as const;
                } else {
                    return {
                        id,
                        isAsync: false,
                        successful: true,
                    } as const;
                }
            } catch (error) {
                // Sync listener threw an error
                errorHandler(`Error in event listener for ${type}:`, error);
                return {
                    id,
                    isAsync: false,
                    successful: false,
                } as const;
            }
        });

        // Create onSettled promise that resolves when all async subscriptions complete
        const onSettled = Promise.all(
            responses.map(async (sub): Promise<SubscriptionResponse> => {
                if (sub.isAsync) {
                    const successful = await sub.successful;
                    return {
                        id: sub.id,
                        isAsync: true,
                        successful,
                    };
                } else {
                    return {
                        id: sub.id,
                        isAsync: false,
                        successful: sub.successful as boolean,
                    };
                }
            })
        );

        return {
            event,
            subscriptions: responses,
            onSettled,
        };
    };

    const on = (
        typeTypesOrListener: string | string[] | EventListener<[IEvent<string, TEvents[string]>]>,
        listener?: EventListener<[IEvent<string, TEvents[string]>]>
    ): Subscription => {
        const subscribeToTypes = typeof typeTypesOrListener === "function"
            ? []
            : Array.isArray(typeTypesOrListener)
                ? typeTypesOrListener
                : [typeTypesOrListener];
        listener = typeof typeTypesOrListener === "function" ? typeTypesOrListener : listener!;

        const listenerId = nanoid();
        if (subscribeToTypes.length === 0) {
            broadcastListeners.set(listenerId, listener);
        }
        subscribeToTypes.forEach((type) => {
            if (!listeners.has(type)) {
                listeners.set(type, new Map<string, (event: IEvent) => void | Promise<void>>());
            }
            const typeListeners = listeners.get(type);
            typeListeners.set(listenerId, listener);
        });

        return {
            id: listenerId,
            unsubscribe: () => {
                const removedTypeSubscriptions = subscribeToTypes.reduce((acc, type) => {
                    const typeListeners = listeners.get(type);
                    const removedSubscription = typeListeners ? typeListeners.delete(listenerId) : false;
                    if (typeListeners && typeListeners.size === 0) {
                        listeners.delete(type);
                    }

                    return acc || removedSubscription;
                }, false);
                const hadBroadcastSubscription = broadcastListeners.delete(listenerId);
                return hadBroadcastSubscription || removedTypeSubscriptions;
            },
        };
    };

    const events = new Proxy({} as any, {
        get: (_target, eventName: string) => ({
            emit: (payload: any) => emitEvent(eventName, payload),
            emitLoose: (payload: any) => emitEvent(eventName, payload),
            on: (listener: any) => on(eventName, listener),
        }),
    });

    return {
        events,
        emit: emitEvent,
        emitLoose: emitEvent,
        on,
    };
}

export function createEventBus<TEmitterMap extends EventBusMap>(emitters:TEmitterMap):IEventBus<TEmitterMap> {
    const subscriptions: Map<string, { listener: EventListener, subscriptions: Subscription[] }> = new Map();
    
    function on(listener: EventListener): Subscription;
    function on<TKeys extends ReadonlyArray<keyof TEmitterMap>>(
        namespaces: TKeys, 
        listener: EventListener<[{ [K in TKeys[number]]: { [E in keyof InferSourceEvents<TEmitterMap[K]>]: IEvent<E & string, InferSourceEvents<TEmitterMap[K]>[E]> }[keyof InferSourceEvents<TEmitterMap[K]>] }[TKeys[number]]]>
    ): Subscription;
    function on(namespacesOrListener: any, listener?: any): Subscription {
        listener = typeof namespacesOrListener === "function" ? namespacesOrListener : listener!;
        const namespaces = typeof namespacesOrListener === "function" ? Object.keys(emitters) : namespacesOrListener;

        const subscriptionId = nanoid();
        const emitterSubscriptions: Subscription[] = namespaces.map((namespace: string) => {
            const emitter = emitters[namespace];
            return emitter.on((payload: any, event: any) => {
                listener(payload, {
                    ...event,
                    namespace,
                });
            });
        });
        subscriptions.set(subscriptionId, { listener, subscriptions: emitterSubscriptions });
        return {
            id: subscriptionId,
            unsubscribe: () => {
                const sub = subscriptions.get(subscriptionId);
                if (!sub) {
                    return false;
                }
                sub.subscriptions.forEach((subscription) => {
                    subscription.unsubscribe();
                });
                subscriptions.delete(subscriptionId);
                return true;
            },
        };
    }
    
    return {
        on,
        namespaces: emitters as TEmitterMap
    }
}